import { LocationEntity, PermissionEntity, PropertyPersonnel, TagStatus } from "@admin/core/models";
import { ItemRecord } from "@controladad/ng-base";
import { format, isValid } from 'date-fns';
import { TreeNode } from "@admin/shared";
import { TagStatusValues } from "@admin/shared/data";
import * as Jalali from 'date-fns-jalali';
import { AppPermission } from "@admin/core";


export function getFromItemRecord<T, U>(items: ItemRecord<T, U>[], value: T | U | undefined | null) {
  return items.find((t) => t.value === value || (t.alt !== undefined && t.alt === value));
}

export function flattenLocationsToItems(locations: LocationEntity[]): ItemRecord<number>[] {
  return locations.flatMap((location) => {
    return [{ value: location.id, label: location.name }, ...flattenLocationsToItems(location.children)];
  });
}

export function convertLocationsToNodes(locations: LocationEntity[], withDetails = false, disableUnavailableLocations = false, currentPlanLocationIds: number[] = []): TreeNode[] {
  return locations.map((location) => {
    const children = convertLocationsToNodes(location.children, withDetails, disableUnavailableLocations, currentPlanLocationIds);
    const allChildrenIds: number[] = [];
    if (children.length > 0) {
      allChildrenIds.push(...collectAllIds(children));
    }
    return {
      id: location.id,
      label: location.name,
      subLabel: withDetails ? location.tagSerial : undefined,
      status: withDetails ? getFromItemRecord(TagStatusValues, location.tagStatus) : undefined,
      children: children,
      allChildrenIds: allChildrenIds.length > 0 ? allChildrenIds : undefined,
      disabled: disableUnavailableLocations ? !location.availableForPlan && !currentPlanLocationIds?.includes(location.id) : false,
    };
  });
}

function collectAllIds(nodes: TreeNode[]): number[] {
  const ids: number[] = [];
  for (const node of nodes) {
    ids.push(node.id);
    if (node.children) {
      ids.push(...collectAllIds(node.children));
    }
  }
  return ids;
}

export function dateToString(date: string | undefined | null) {
  return !!date && isValid(new Date(date)) ? new Date(new Date(date)).toISOString() : null;
}

export function formatToJalaliDate(date: string | undefined | null, format = 'yyyy/MM/dd, hh:mm a') {
  return !!date && isValid(new Date(date)) ? Jalali.format(date, format) : '';
}

export function getAllAppPermissions(permissions: PermissionEntity[]): AppPermission[] {
  return permissions.flatMap(module =>
    module.permissions.map(permission => ({ id: permission.id, key: permission.key }))
  );
}

export function getUserPermissionKeys(permissions?: AppPermission[], userPermissions?: number[]): string[] {
  if (!userPermissions) return [];
  return permissions?.filter(x => userPermissions.includes(x.id)).map(x => x.key) || [];
}

export function personnelToString(personnel?: PropertyPersonnel, index?: number): string {
  if (!personnel) return '';
  const prefix = index === undefined ? '' : `${index === 0 ? 'اصلی' : index === 1 ? 'دوم' : ''}: `;
  return `${prefix}${personnel.fullName} (${personnel.personnelCode})`;
}

export function personnelArrayToStringArray(array: PropertyPersonnel[] = []) {
  return array.map((p, i) => personnelToString(p, i));
}

export function personnelArrayToString(array: PropertyPersonnel[] = [], separator = '\n') {
  return array.map((p, i) => personnelToString(p, i)).join(separator);
}

export function getLabel(value: any, items: ItemRecord<any>[]) {
  return items.find((item) => item.value === value)?.label || '-';
}

export function isChangingTagStatusValid(prev: TagStatus, next: TagStatus, displayError = false) {
  if (prev === next) return true;
  if (next === TagStatus.PendingAttachment) return true;
  if (prev === TagStatus.PendingApproval && next === TagStatus.HasTag) return true;
  if (displayError) {
    const prevLabel = getLabel(prev, TagStatusValues);
    const nextLabel = getLabel(next, TagStatusValues);
    snackbar$.error(`وضعیت تگ ${prevLabel} به ${nextLabel} قابل تغییر نیست`);
  }
  return false;
}